import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output
} from '@angular/core';

import { MatIconModule } from '@angular/material/icon';

import {
  EntityPickBlock,
  RichContentEntityType
} from '@core/services/ai/ai-assistant.service';
import { defaultIconFor } from '../entity-deeplink.helper';

@Component({
  selector: 'app-ai-entity-pick-block',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatIconModule],
  template: `
    @if (block.title) {
      <div class="ep__title">{{ block.title }}</div>
    }
    @if (block.prompt) {
      <p class="ep__prompt">{{ block.prompt }}</p>
    }
    <ul class="ep">
      @for (o of block.options; track o.id) {
        <li class="ep__row">
          <button type="button" class="ep__btn" (click)="onPick(o)">
            <mat-icon class="ep__icon">{{
              o.icon || iconFor(o.entityType)
            }}</mat-icon>
            <div class="ep__main">
              <div class="ep__head">
                <span class="ep__name">{{ o.title }}</span>
                @if (o.badge) {
                  <span class="ep__badge" [attr.data-tone]="o.badge.tone">{{
                    o.badge.label
                  }}</span>
                }
              </div>
              @if (o.subtitle) {
                <div class="ep__sub">{{ o.subtitle }}</div>
              }
            </div>
            <mat-icon class="ep__chevron">chevron_right</mat-icon>
          </button>
        </li>
      }
    </ul>
  `,
  styles: [
    `
      :host {
        display: block;
      }
      .ep__title {
        font-size: 12px;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        color: rgba(15, 23, 42, 0.55);
        margin: 0 0 6px;
      }
      .ep__prompt {
        margin: 0 0 8px;
        font-size: 14px;
        color: rgba(15, 23, 42, 0.85);
        overflow-wrap: anywhere;
      }
      .ep {
        list-style: none;
        margin: 0;
        padding: 0;
        background: #fff;
        border: 1px solid rgba(15, 23, 42, 0.08);
        border-radius: 12px;
        overflow: hidden;
      }
      .ep__row + .ep__row {
        border-top: 1px solid rgba(15, 23, 42, 0.06);
      }
      .ep__btn {
        width: 100%;
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 10px 12px;
        background: transparent;
        border: none;
        cursor: pointer;
        text-align: left;
        color: inherit;
        font: inherit;
      }
      .ep__btn:hover {
        background: rgba(99, 102, 241, 0.06);
      }
      .ep__btn:focus-visible {
        outline: 2px solid #6366f1;
        outline-offset: -2px;
      }
      .ep__icon {
        flex-shrink: 0;
        font-size: 22px;
        width: 22px;
        height: 22px;
        color: #6366f1;
      }
      .ep__main {
        flex: 1;
        min-width: 0;
      }
      .ep__head {
        display: flex;
        align-items: center;
        gap: 8px;
        flex-wrap: wrap;
      }
      .ep__name {
        font-weight: 600;
        font-size: 14px;
        color: rgba(15, 23, 42, 0.92);
        overflow-wrap: anywhere;
      }
      .ep__sub {
        font-size: 12px;
        color: rgba(15, 23, 42, 0.55);
        margin-top: 2px;
        overflow-wrap: anywhere;
      }
      .ep__badge {
        font-size: 11px;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        padding: 2px 6px;
        border-radius: 999px;
        background: rgba(99, 102, 241, 0.12);
        color: #4338ca;
      }
      .ep__badge[data-tone='positive'] {
        background: rgba(16, 185, 129, 0.12);
        color: #047857;
      }
      .ep__badge[data-tone='warning'] {
        background: rgba(245, 158, 11, 0.12);
        color: #b45309;
      }
      .ep__badge[data-tone='danger'] {
        background: rgba(239, 68, 68, 0.12);
        color: #b91c1c;
      }
      .ep__chevron {
        flex-shrink: 0;
        font-size: 20px;
        width: 20px;
        height: 20px;
        color: rgba(15, 23, 42, 0.4);
      }
    `
  ]
})
export class AiEntityPickBlockComponent {
  @Input({ required: true }) block!: EntityPickBlock;
  @Output() readonly picked = new EventEmitter<string>();

  iconFor(entityType: RichContentEntityType): string {
    return defaultIconFor(entityType);
  }

  onPick(option: EntityPickBlock['options'][number]): void {
    const text =
      option.message ?? `Selected ${option.entityType} ${option.title}`;
    this.picked.emit(text);
  }
}
